import React from 'react';
import { I18n } from '@aws-amplify/core';

import { useGetDataExtraction } from '@/hooks';
import { Drawer } from '@/components/ui/Drawer';
import { Loading } from '@/components/ui/Loading';
import { GeneralInfo } from './GeneralInfo';
import { RenderContent } from './RenderContent';
import * as S from './styles';

export interface DataExtractionDetailsProps {
	isOpen?: boolean;
	data: {
		id: string;
		organization_id: string;
		company_id: string;
		status?: string;
		originalStatus?: string;
	};
	onClose: () => void;
}

export function DataExtractionDetails({
	isOpen = false,
	data,
	onClose
}: Readonly<DataExtractionDetailsProps>): JSX.Element {
	const { isLoading, data: extractionData } = useGetDataExtraction({
		organization_id: data.organization_id,
		company_id: data.company_id,
		data_extraction_id: data.id
	});

	if (isLoading || !extractionData) {
		return (
			<Drawer title="" open={isOpen} onClose={onClose}>
				<Loading size="large" />
			</Drawer>
		);
	}

	const actionPlanData = extractionData?.dataExtractionReportsActionPlanColumns;
	const report_selected = extractionData?.custom_report?.name;
	const reviewActionPlan = extractionData?.has_action_plan_extraction || false;
	const actionPlanReports = extractionData?.data_extraction_action_plan_origins || [];
	const extractionTools = extractionData?.data_extraction_tools || [];

	function renderExtractionTools(): JSX.Element[] | null {
		if (extractionTools.length === 0) return null;

		return extractionTools.map((tool, index) => (
			<S.ContentTag key={`tool-${index}`}>
				{I18n.get('Results')} {tool.label}
			</S.ContentTag>
		));
	}

	function renderActionPlans(): JSX.Element | JSX.Element[] {
		if (reviewActionPlan && report_selected) {
			return (
				<S.ContentTag key={`action-${report_selected}`}>
					{I18n.get('Action plans')} {report_selected}
				</S.ContentTag>
			);
		}

		return actionPlanReports.map((actionPlan, index) => (
			<S.ContentTag key={`action-${index}`}>
				{I18n.get('Action plans')}
				{actionPlan.custom_report_name || ''}
			</S.ContentTag>
		));
	}

	function renderNoContentMessage(): JSX.Element | null {
		if (report_selected || actionPlanReports.length > 0) return null;

		return <p>{I18n.get('No content available')}</p>;
	}

	function renderReportSelected(): JSX.Element | null {
		if (!report_selected) return null;

		return (
			<S.ContentTag key={`result-${report_selected}`}>
				{I18n.get('Results')} {report_selected}
			</S.ContentTag>
		);
	}

	return (
		<Drawer title={extractionData.name} open={isOpen} onClose={onClose}>
			<GeneralInfo extractionData={extractionData} rowData={data} />
			<S.ContentContainer>
				<S.ContentTitle>{I18n.get('Content:')}</S.ContentTitle>
				{renderReportSelected()}
				{renderExtractionTools()}
				{renderActionPlans()}
				{renderNoContentMessage()}
			</S.ContentContainer>
			{actionPlanData && RenderContent(I18n.get('Action plans content:'), actionPlanData[0]?.formatted_columns)}
		</Drawer>
	);
}
